/*
  The population breakdown response contract (§9.1, §9.2, D13).

  The endpoint is a separate backend MR (D11) and returns 404 in production today, so
  this is written against the contract and tested directly rather than through a mocking
  library — §9.1 explains why msw cannot load under CRA 5's Jest 27.

  Available means a 200 whose body parses to the shape below. Everything else hides the
  band: any non-2xx, a network or CORS failure, or a body that does not parse. Hidden
  means no band, no error UI and no layout shift, because that is production until D11
  merges and it has to look intentional.

  An all-zero 200 is AVAILABLE and renders as zeros. That is real data — a set with
  nothing graded — and hiding it would misreport. The "never show a zeroed band" rule is
  about never substituting zeros for a response that failed or never arrived.
*/

export type BreakdownScope = "global" | "game" | "set";

export interface BreakdownFigures {
  inCatalog: number;
  gradedLinked: number;
  manuallyEntered: number;
  inGrading: number;
  scope: BreakdownScope;
  /* True when manuallyEntered was attributed by string match — drives the ≈ marker. */
  approximate: boolean;
}

export type BreakdownState =
  | { available: true; figures: BreakdownFigures }
  | { available: false };

const HIDDEN: BreakdownState = { available: false };

const SCOPES: BreakdownScope[] = ["global", "game", "set"];

const isCount = (value: unknown): value is number =>
  typeof value === "number" && isFinite(value) && Math.floor(value) === value;

/*
  status is number | string because that is what the caller has. RTK Query's
  fetchBaseQuery surfaces production's HTML 404 as { status: "PARSING_ERROR",
  originalStatus: 404 }, and a fetch failure as { status: "FETCH_ERROR" }. A status that
  is not a 2xx number — including any of those strings — hides the band.
*/
const isSuccessStatus = (status: number | string | null | undefined): boolean => {
  const code = typeof status === "string" ? Number(status) : status;
  return typeof code === "number" && isFinite(code) && code >= 200 && code < 300;
};

/*
  A body arriving as a string is parsed once: a mock or a proxy serving the JSON as
  text/plain is still a conforming response, and an unparseable string is hidden by the
  shape check below like any other malformed body.
*/
const asObject = (body: unknown): Record<string, unknown> | null => {
  let value = body;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch (e) {
      return null;
    }
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

export const parseBreakdown = (
  status: number | string | null | undefined,
  body: unknown
): BreakdownState => {
  if (!isSuccessStatus(status)) {
    return HIDDEN;
  }

  const parsed = asObject(body);
  if (!parsed) {
    return HIDDEN;
  }

  const { inCatalog, gradedLinked, manuallyEntered, inGrading, scope, approximate } =
    parsed;

  if (
    !isCount(inCatalog) ||
    !isCount(gradedLinked) ||
    !isCount(manuallyEntered) ||
    !isCount(inGrading) ||
    typeof scope !== "string" ||
    SCOPES.indexOf(scope as BreakdownScope) === -1 ||
    typeof approximate !== "boolean"
  ) {
    return HIDDEN;
  }

  return {
    available: true,
    figures: {
      inCatalog,
      gradedLinked,
      manuallyEntered,
      inGrading,
      scope: scope as BreakdownScope,
      approximate,
    },
  };
};
